import React from 'react';
import { Box, Typography, Container, Link, Grid, IconButton, TextField, Button, useTheme } from '@mui/material';
import { LinkedIn as LinkedInIcon, Facebook as FacebookIcon, YouTube as YouTubeIcon, Mail as MailIcon, Language as GlobeIcon } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

const Footer = () => {
  const theme = useTheme();
  const mode = theme.palette.mode;

  const sections = {
    'Insights': [
      { title: 'Web Development', path: '/blogs' },
      { title: 'Cloud Automation', path: '/blogs' },
      { title: 'AI & Data Quality', path: '/blogs' },
      { title: 'Software Engineering', path: '/blogs' },
    ],
    'Resources': [
      { title: 'Utility Tools', path: '/tools' },
      { title: 'Red Flag Detector', path: '/tools/rent-agreement-detector' },
      { title: 'Services', path: '/services' },
      { title: 'About Us', path: '/about' },
    ],
  };

  return (
    <Box
      sx={{
        backgroundColor: mode === 'light' ? '#FFFFFF' : '#0B0F19',
        py: { xs: 6, md: 8 },
        mt: 'auto',
        borderTop: `1px solid ${mode === 'light' ? 'rgba(226, 232, 240, 0.8)' : 'rgba(255, 255, 255, 0.06)'}`,
        transition: 'background-color 0.3s ease, border-color 0.3s ease',
      }}
      component="footer"
    >
      <Container maxWidth="lg">
        <Grid container spacing={4} justifyContent="space-between">
          <Grid item xs={12} md={4}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 800, 
                mb: 2, 
                fontFamily: '"Outfit", sans-serif',
                letterSpacing: '-0.02em'
              }}
            >
              CoreTechTalks
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: '320px', lineHeight: 1.6 }}>
              Your essential platform for modern technology guides, workflow automation services, and intelligent AI utilities.
            </Typography>
            
            {/* Social Icons */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton 
                href="https://www.linkedin.com/company/135234619/admin/dashboard/" 
                target="_blank" 
                size="small"
                sx={{ 
                  color: 'text.secondary',
                  '&:hover': { color: 'primary.main', transform: 'scale(1.15)' },
                  transition: 'color 0.2s, transform 0.2s'
                }}
              >
                <LinkedInIcon fontSize="small" />
              </IconButton>
              <IconButton 
                href="https://www.facebook.com/profile.php?id=61591771430289" 
                target="_blank" 
                size="small"
                sx={{ 
                  color: 'text.secondary',
                  '&:hover': { color: 'primary.main', transform: 'scale(1.15)' },
                  transition: 'color 0.2s, transform 0.2s'
                }}
              >
                <FacebookIcon fontSize="small" />
              </IconButton>
              <IconButton 
                href="https://www.youtube.com/channel/UCNr1PjfDekIfS8_YTjeaX5w" 
                target="_blank" 
                size="small"
                sx={{ 
                  color: 'text.secondary',
                  '&:hover': { color: 'primary.main', transform: 'scale(1.15)' },
                  transition: 'color 0.2s, transform 0.2s'
                }}
              >
                <YouTubeIcon fontSize="small" />
              </IconButton>
            </Box>
          </Grid>
          
          {Object.keys(sections).map((sectionTitle) => (
            <Grid item xs={6} sm={4} md={2} key={sectionTitle}>
              <Typography 
                variant="subtitle2" 
                sx={{ 
                  fontWeight: 700, 
                  mb: 2, 
                  color: 'text.primary',
                  fontFamily: '"Plus Jakarta Sans", sans-serif',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase'
                }}
              >
                {sectionTitle}
              </Typography>
              {sections[sectionTitle].map((link) => (
                <Link 
                  key={link.title} 
                  href={link.path} 
                  color="text.secondary" 
                  display="block" 
                  sx={{ 
                    mb: 1.5, 
                    fontSize: '14px',
                    textDecoration: 'none',
                    transition: 'color 0.2s',
                    '&:hover': { color: 'primary.main' } 
                  }}
                >
                  {link.title}
                </Link>
              ))}
            </Grid>
          ))}
          
          {/* Newsletter Column */}
          <Grid item xs={12} sm={8} md={3}>
            <Typography 
              variant="subtitle2" 
              sx={{ 
                fontWeight: 700, 
                mb: 2, 
                color: 'text.primary',
                fontFamily: '"Plus Jakarta Sans", sans-serif',
                letterSpacing: '0.05em',
                textTransform: 'uppercase'
              }}
            >
              Stay Updated
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '14px' }}>
              Subscribe to get notified of new tools and articles.
            </Typography>
            <Box 
              component="form" 
              onSubmit={(e) => e.preventDefault()} 
              sx={{ 
                display: 'flex', 
                gap: 1,
                bgcolor: mode === 'light' ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.02)',
                p: '4px',
                borderRadius: '12px',
                border: `1px solid ${mode === 'light' ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)'}`
              }}
            >
              <TextField 
                placeholder="Enter email" 
                variant="standard" 
                size="small"
                fullWidth
                InputProps={{ 
                  disableUnderline: true,
                  startAdornment: <MailIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
                  sx: { fontSize: '14px', px: 1 }
                }}
              />
              <Button 
                variant="contained" 
                size="small" 
                type="submit"
                sx={{ 
                  borderRadius: '8px', 
                  minWidth: 'auto',
                  px: 2,
                  py: 1
                }}
              >
                Join
              </Button>
            </Box>
          </Grid>
        </Grid>
        
        <Box 
          sx={{ 
            mt: 6, 
            pt: 4, 
            borderTop: `1px solid ${mode === 'light' ? 'rgba(226, 232, 240, 0.6)' : 'rgba(255, 255, 255, 0.04)'}`, 
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            textAlign: 'center' 
          }}
        >
          {/* Quick Links */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: { xs: 2, sm: 4 }, fontSize: '13px' }}>
            <Link component={RouterLink} to="/privacy" color="text.secondary" sx={{ textDecoration: 'none', '&:hover': { color: 'primary.main' } }}>Privacy Notice</Link>
            <Link component={RouterLink} to="/terms" color="text.secondary" sx={{ textDecoration: 'none', '&:hover': { color: 'primary.main' } }}>Terms & Conditions</Link>
            <Link component={RouterLink} to="/contact" color="text.secondary" sx={{ textDecoration: 'none', '&:hover': { color: 'primary.main' } }}>Contact Us</Link>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary', cursor: 'pointer', '&:hover': { color: 'primary.main' } }}>
              <GlobeIcon sx={{ fontSize: '16px' }} />
              <Typography variant="body2" sx={{ fontSize: '13px' }}>English</Typography>
            </Box>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px' }}>
            © {new Date().getFullYear()} CoreTechTalks &mdash; Crafted with ❤️ to share technical insights and utility tools.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
